/**
 * Finds the `{{name}}` markers a query takes its run values through.
 *
 * `{{name}}` was chosen because it cannot collide with anything Athena accepts: a brace is not a
 * token Trino allows where an expression belongs — `SELECT {{x}}` fails to parse with "mismatched
 * input '{'" — so an unrendered marker can never reach Athena and look like it worked.
 *
 * Nothing here knows which names are legal — that is the caller's question. This module only
 * finds markers and skips the places a `{{` is just text.
 */

export interface FoundMarker {
  name: string;
  start: number;
  /** Offset one past the closing brace. */
  end: number;
}

export interface QueryMarkerScan {
  /** Every well-formed marker, in the order it appears, including repeats. */
  markers: FoundMarker[];
  /**
   * A `{{` that does not open a valid marker. Reported rather than ignored: left alone it reaches
   * Athena as a brace and comes back as a parse error that says nothing about markers.
   */
  malformed: { start: number }[];
}

// Surrounding whitespace is accepted because `{{ report_from_date }}` is what people type, and
// rejecting it as a malformed marker would be baffling.
const ANCHORED_MARKER = /^\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/;

export const scanQueryMarkers = (sql: string): QueryMarkerScan => {
  const scan: QueryMarkerScan = { markers: [], malformed: [] };

  for (const { start: spanStart, end: spanEnd } of codeSpans(sql)) {
    let cursor = spanStart;
    for (let open = sql.indexOf('{{', cursor); open !== -1 && open < spanEnd; open = sql.indexOf('{{', cursor)) {
      const match = ANCHORED_MARKER.exec(sql.slice(open, spanEnd));
      if (!match) {
        scan.malformed.push({ start: open });
        cursor = open + 2;
        continue;
      }
      const [text, name] = match;
      scan.markers.push({ name, start: open, end: open + text.length });
      cursor = open + text.length;
    }
  }

  return scan;
};

/** Deduped marker names, in the order they first appear in the SQL. */
export const queryMarkerNamesUsed = (sql: string): string[] => [
  ...new Set(scanQueryMarkers(sql).markers.map((marker) => marker.name)),
];

/**
 * Finds reads of the query clock. What to make of them is the caller's policy; recognising them
 * lives here because it needs this module's notion of which text is code.
 *
 * Deliberately lexical, and only as good as that makes it: someone set on reaching the clock can
 * do so through something this does not recognise.
 */
const CLOCK_KEYWORD = /\b(current_timestamp|current_time|current_date|localtimestamp|localtime|now)\b/gi;

export interface FoundClockRead {
  /** Lower-cased function name, e.g. 'current_date'. */
  name: string;
  start: number;
  /** Offset one past the last character of the name. */
  end: number;
}

/** Every clock read in the SQL, in order, including repeats — offsets let a caller underline each. */
export const findClockFunctions = (sql: string): FoundClockRead[] => {
  const found: FoundClockRead[] = [];
  for (const { start, end } of codeSpans(sql)) {
    const code = sql.slice(start, end);
    for (const match of code.matchAll(CLOCK_KEYWORD)) {
      const name = match[1].toLowerCase();
      // The keywords read the clock bare, but `now` is only a clock read as a call — `now` alone
      // is a legal column name.
      if (name === 'now' && !/^\s*\(/.test(code.slice(match.index + match[1].length))) {
        continue;
      }
      found.push({ name, start: start + match.index, end: start + match.index + match[1].length });
    }
  }
  return found;
};

/** Deduped, lower-cased names of the clock functions the SQL reads. */
export const clockFunctionNamesUsed = (sql: string): string[] => [
  ...new Set(findClockFunctions(sql).map((read) => read.name)),
];

interface CodeSpan {
  start: number;
  end: number;
}

/**
 * The regions of the SQL that are code, leaving out single-quoted strings, double-quoted
 * identifiers, `--` line comments and `/* *\/` blocks. A marker or a clock function inside any of
 * those is text the author wrote on purpose — and a quoted identifier is also the escape hatch for
 * a column genuinely named `current_date`.
 */
const codeSpans = (sql: string): CodeSpan[] => {
  const spans: CodeSpan[] = [];
  let spanStart = 0;
  let cursor = 0;

  while (cursor < sql.length) {
    if (startsNonCode(sql, cursor)) {
      spans.push({ start: spanStart, end: cursor });
      cursor = endOfNonCode(sql, cursor);
      spanStart = cursor;
    } else {
      cursor += 1;
    }
  }

  spans.push({ start: spanStart, end: sql.length });
  return spans;
};

const startsNonCode = (sql: string, at: number): boolean =>
  sql.startsWith('--', at) || sql.startsWith('/*', at) || sql[at] === "'" || sql[at] === '"';

const endOfNonCode = (sql: string, start: number): number => {
  if (sql.startsWith('--', start)) {
    const newline = sql.indexOf('\n', start);
    return newline === -1 ? sql.length : newline + 1;
  }
  if (sql.startsWith('/*', start)) {
    const close = sql.indexOf('*/', start + 2);
    return close === -1 ? sql.length : close + 2;
  }

  const quote = sql[start];
  let cursor = start + 1;
  while (cursor < sql.length) {
    if (sql[cursor] === quote) {
      // Doubling is the only escape in both grammars, so a pair continues the literal.
      if (sql[cursor + 1] === quote) {
        cursor += 2;
        continue;
      }
      return cursor + 1;
    }
    cursor += 1;
  }
  // Unterminated: treat the rest as non-code. Athena will reject the statement anyway, and
  // scanning on would find "markers" inside what the author meant as a string.
  return sql.length;
};
